"use client";

import { Button } from "@pex/ui-web/components/ui/button";
import { Send } from "lucide-react";
import { useState } from "react";

type Status = "idle" | "loading" | "revealed" | "error";

interface Props {
  className?: string;
  groupId: string;
  size?: "default" | "sm";
}

function buttonLabel(status: Status): string {
  if (status === "loading") {
    return "Loading…";
  }
  if (status === "error") {
    return "Try again";
  }
  return "Contact";
}

/**
 * Reveals a group's contact email only after the user clicks. The address is
 * never part of the page's server-rendered HTML or hydration payload — it is
 * fetched on demand from GET /api/groups/[id]/contact — so it can't be bulk
 * harvested by email-scraping bots (GH #713). Render only when the group is
 * known to have a contact email (`group.has_contact_email`).
 */
export function ContactGroupButton({
  groupId,
  size = "default",
  className,
}: Props) {
  const [status, setStatus] = useState<Status>("idle");
  const [email, setEmail] = useState<string | null>(null);

  async function reveal() {
    setStatus("loading");
    try {
      const response = await fetch(`/api/groups/${groupId}/contact`, {
        cache: "no-store",
      });
      if (!response.ok) {
        setStatus("error");
        return;
      }
      const data = (await response.json()) as { email?: string };
      if (!data.email) {
        setStatus("error");
        return;
      }
      setEmail(data.email);
      setStatus("revealed");
    } catch {
      setStatus("error");
    }
  }

  if (status === "revealed" && email) {
    return (
      <Button asChild className={className} size={size} variant="outline">
        <a href={`mailto:${email}`}>
          <Send />
          <span className="min-w-0 truncate">{email}</span>
        </a>
      </Button>
    );
  }

  return (
    <Button
      aria-busy={status === "loading"}
      className={className}
      disabled={status === "loading"}
      onClick={reveal}
      size={size}
      type="button"
      variant="outline"
    >
      <Send />
      {buttonLabel(status)}
    </Button>
  );
}
